import { useCallback, useEffect, useRef, useState } from 'react';
import { genUUIDV4 } from '@frontend/string';
import { useDraftApi } from '../hooks/api-hooks/use-draft-api';
import { AutosaveCoordinator, AutosaveState, createAutosaveCoordinator } from './autosave-coordinator';
import { DRAFT_PAYLOAD_VERSION } from './draft-serializer';
import { trackAutosaveEvent } from './draft-telemetry';
import { DraftMetadata } from './draft.types';

interface UseDraftAutosaveArgs {
  companyId: string;
  submissionId: string;
  /** Autosave runs only while enabled (eligible, draft existence known, form being filled). */
  enabled: boolean;
}

type DraftLifecycle = Pick<DraftMetadata, 'draftId' | 'version'>;

const IDLE_STATE: AutosaveState = { status: 'idle' };

/**
 * Owns the autosave coordinator and draft lifecycle (draftId + version) for one submission.
 * The coordinator is recreated whenever autosave is re-enabled; the lifecycle survives until reset.
 */
export const useDraftAutosave = ({ companyId, submissionId, enabled }: UseDraftAutosaveArgs) => {
  const { saveDraft } = useDraftApi({ companyId, submissionId });
  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;
  const submissionIdRef = useRef(submissionId);
  submissionIdRef.current = submissionId;

  const lifecycleRef = useRef<DraftLifecycle | null>(null);
  const [coordinator, setCoordinator] = useState<AutosaveCoordinator | null>(null);
  const [state, setState] = useState<AutosaveState>(IDLE_STATE);

  useEffect(() => {
    if (!enabled) return;

    const instance = createAutosaveCoordinator({
      save: async (snapshot) => {
        // The first save of a lifecycle mints the draftId (spec §4).
        const lifecycle = lifecycleRef.current ?? { draftId: genUUIDV4(), version: 0 };
        lifecycleRef.current = lifecycle;
        const version = lifecycle.version + 1;

        await saveDraftRef.current(snapshot, {
          draftId: lifecycle.draftId,
          version,
          submissionId: submissionIdRef.current,
          savedAt: new Date().toISOString(),
          payloadVersion: DRAFT_PAYLOAD_VERSION,
        });
        lifecycle.version = version;
      },
      onEvent: trackAutosaveEvent,
    });

    const unsubscribe = instance.subscribe(setState);
    setState(instance.getState());
    setCoordinator(instance);

    return () => {
      unsubscribe();
      instance.dispose();
      setCoordinator(null);
      setState(IDLE_STATE);
    };
  }, [enabled]);

  /** Continues the lifecycle of a restored draft so later saves keep its draftId. */
  const adoptDraft = useCallback((lifecycle: DraftLifecycle) => {
    lifecycleRef.current = { ...lifecycle };
  }, []);

  /** Starts a new lifecycle after a discard; the next save mints a new draftId. */
  const resetLifecycle = useCallback(() => {
    lifecycleRef.current = null;
  }, []);

  return { coordinator, status: state.status, lastSavedAt: state.lastSavedAt, adoptDraft, resetLifecycle };
};
